// Identities API
import type { CaseRole } from './cases'
import { apiClient } from './client'

/** Mirrors the v1 backend whitelist (`EDITABLE_IDENTITY_FIELDS`). */
export interface EditableIdentityFields {
  name?: string
  nickname?: string
  firstName?: string
  lastName?: string
  personalNumber?: string
  birthDate?: string
  placeOfBirth?: string
  address?: string
  maidenName?: string
  email?: string
  phone?: string
}

export interface Identity {
  id: string
  name: string
  type: string
  verified: boolean
  documents?: unknown[]
  accounts?: unknown[]
}

export interface IdentityStats {
  total: number
  verified: number
  unverified: number
}

/** The person's main photo after the pick — what the avatar now reads. */
export interface IdentityMainPhoto {
  person_id: string
  face_id: string
  photo_url: string | null
}

/** One tile of the *Unassigned faces* tab (PRD-0031 slice 04). */
export interface UnassignedFace {
  face_id: string
  crop_url: string | null
  document_id: string | null
  document_name: string | null
  /** The stored page index, 0 for a photo or a recording: `facePageNumber` names it. */
  page: number | null
  /** The image the crop was cut from — a PDF gets its stored page render. */
  source_url: string | null
  box: number[]
  quality_flags: string[]
  pending_suggestions: number
  /** `"video"` for a pass of a recording (ADR-0085); null for a photo's face. */
  source: string | null
  /** Seconds into the recording where the pass starts; null for a photo's face. */
  t_start: number | null
  /** Where the cluster's last pass ends; null for a photo's face, absent on an older API. */
  t_end?: number | null
  /** The cluster of passes the tile stands for (PRD-0074 slice 04); null for a photo's face. */
  cluster_id?: string | null
  /** How many passes the tile stands for; 1 for a photo's face. */
  cluster_size?: number
  /** Every face the tile stands for, the best shot first — what an assign of the tile confirms. */
  face_ids?: string[]
}

export interface UnassignedFacesPage {
  faces: UnassignedFace[]
  total: number
  page: number
  page_size: number
  /** The caller's role as the read was granted it; a viewer is offered no action. Absent on an older API. */
  member_role?: CaseRole
}

/** The identity after a manual create or an assign. */
export interface ManualIdentity {
  person_id: string
  /** The composed "first last" the list and the graph read. */
  name: string | null
  first_name: string | null
  last_name: string | null
  nickname: string | null
  unnamed_seq: number | null
  confirmed_face_ids: string[]
}

export const identitiesApi = {
  // V1 — list (server-paginated), get, update
  list: (caseId: string, params: { skip?: number; limit?: number; q?: string } = {}) =>
    apiClient.get<Identity[]>(`/v1/cases/${caseId}/identities`, {
      params: {
        skip: params.skip ?? 0,
        limit: params.limit ?? 50,
        ...(params.q ? { q: params.q } : {}),
      },
    }),

  get: (caseId: string, identityId: string) =>
    apiClient.get<Identity>(`/v1/cases/${caseId}/identities/${identityId}`),

  update: (caseId: string, identityId: string, patch: Partial<EditableIdentityFields>) =>
    apiClient.patch<Identity>(`/v1/cases/${caseId}/identities/${identityId}`, patch),

  /** Faces of the case nobody is confirmed on, newest first (SC-006). */
  unassignedFaces: (caseId: string, params: { page?: number; pageSize?: number } = {}) =>
    apiClient.get<UnassignedFacesPage>(`/v1/cases/${caseId}/unassigned-faces`, {
      params: { page: params.page ?? 1, page_size: params.pageSize ?? 25 },
    }),

  /**
   * Create a manual identity out of a face selection (SC-007). Each naming
   * field is optional on its own, but all three blank is a 422 — a manual
   * person is never created nameless. A face confirmed elsewhere is a 409 and
   * nothing is created.
   */
  createFromFaces: (
    caseId: string,
    body: { first_name?: string; last_name?: string; nickname?: string; face_ids: string[] }
  ) => apiClient.post<ManualIdentity>(`/v1/cases/${caseId}/identities/from-faces`, body),

  /** Confirm a face selection to an identity that already exists (SC-009). */
  assignFaces: (caseId: string, identityId: string, faceIds: string[]) =>
    apiClient.post<ManualIdentity>(`/v1/cases/${caseId}/identities/${identityId}/faces`, {
      face_ids: faceIds,
    }),

  /** Point the avatar at one of the person's confirmed faces; a foreign face is a 409. */
  setMainPhoto: (caseId: string, identityId: string, faceId: string) =>
    apiClient.put<IdentityMainPhoto>(`/v1/cases/${caseId}/identities/${identityId}/main-photo`, {
      face_id: faceId,
    }),

  // Legacy (not yet migrated to v1) — verify/remove ID document
  verifyDocument: (caseId: string, identityId: string, documentId: string) =>
    apiClient.post<void>(`/cases/${caseId}/identities/${identityId}/verify-document`, undefined, {
      params: { document_id: documentId },
    }),

  removeDocument: (caseId: string, identityId: string, documentId: string) =>
    apiClient.post<void>(`/cases/${caseId}/identities/${identityId}/remove-document`, undefined, {
      params: { document_id: documentId },
    }),
}
